import React, { useRef, useState, useEffect } from "react";
import * as htmlToImage from "html-to-image";
import { Download, Copy, X, WhatsappLogo, TwitterLogo, ShareNetwork, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Universal "Share as Image" modal.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <button onClick={() => setOpen(true)}>Share</button>
 *   <ShareCardModal
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     title="Share your trade setup"
 *     shareText="My trade plan via Breakout Scanner — check it out!"
 *     shareUrl="https://breakoutscanner.co.in/risk-calculator"
 *     filename="trade-setup.png"
 *   >
 *     <ShareTradeSetupCard …props />
 *   </ShareCardModal>
 *
 * The modal captures whatever you pass as children via `html-to-image` and
 * presents Download / Copy / WhatsApp / Twitter actions.
 */
export default function ShareCardModal({
  open,
  onClose,
  children,
  title = "Share",
  shareText = "Check this out from Breakout Scanner",
  shareUrl = "https://breakoutscanner.co.in",
  filename = "breakout-scanner-card.png",
}) {
  const cardRef = useRef(null);
  const [pngUrl, setPngUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copiedImage, setCopiedImage] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Generate PNG when modal opens (after a small delay so fonts/icons are ready)
  useEffect(() => {
    if (!open) {
      setPngUrl(null);
      return;
    }
    let cancelled = false;
    const generate = async () => {
      setGenerating(true);
      // Wait for the card to render + fonts to settle
      await new Promise(r => setTimeout(r, 300));
      try {
        if (!cardRef.current) return;
        const dataUrl = await htmlToImage.toPng(cardRef.current, {
          pixelRatio: 2,
          cacheBust: true,
          backgroundColor: "#0a0a0a",
        });
        if (!cancelled) setPngUrl(dataUrl);
      } catch (e) {
        console.error("share image generation failed:", e);
        if (!cancelled) toast.error("Image generation failed — try the Download button instead");
      } finally {
        if (!cancelled) setGenerating(false);
      }
    };
    generate();
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const download = () => {
    if (!pngUrl) return;
    const a = document.createElement("a");
    a.href = pngUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("Image downloaded");
  };

  const copyImage = async () => {
    if (!pngUrl) return;
    try {
      const blob = await (await fetch(pngUrl)).blob();
      await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
      setCopiedImage(true);
      toast.success("Image copied to clipboard");
      setTimeout(() => setCopiedImage(false), 1500);
    } catch (e) {
      toast.error("Clipboard image not supported here — use Download");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedLink(true);
      toast.success("Link copied");
      setTimeout(() => setCopiedLink(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  const shareNative = async () => {
    if (!navigator.share) return false;
    try {
      // Share with the image file if supported
      if (pngUrl && navigator.canShare) {
        const blob = await (await fetch(pngUrl)).blob();
        const file = new File([blob], filename, { type: blob.type });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ title, text: shareText, url: shareUrl, files: [file] });
          return true;
        }
      }
      await navigator.share({ title, text: shareText, url: shareUrl });
      return true;
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Share failed");
      return false;
    }
  };

  const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + "\n\n" + shareUrl)}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/75 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      onClick={onClose}
      data-testid="share-modal"
    >
      <div
        className="bg-surface border border-subtle rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-subtle">
          <div className="flex items-center gap-2">
            <ShareNetwork size={14} weight="bold" className="text-pos" />
            <div className="text-sm font-medium">{title}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="share-modal-close"
            className="p-1.5 text-muted-fg hover:text-primary-fg rounded-sm hover:bg-surface-hover"
          >
            <X size={14} />
          </button>
        </div>

        {/* Hidden render-source for html-to-image (off-screen, normal flow) */}
        <div className="absolute -left-[99999px] top-0 pointer-events-none" aria-hidden>
          <div ref={cardRef}>{children}</div>
        </div>

        {/* Preview */}
        <div className="p-4 bg-app/60">
          {generating && (
            <div className="aspect-[1.91/1] border border-subtle rounded-sm flex items-center justify-center text-xs font-mono uppercase tracking-widest text-muted-fg animate-pulse">
              /// Generating image…
            </div>
          )}
          {!generating && pngUrl && (
            <img
              src={pngUrl}
              alt="Share preview"
              className="w-full rounded-sm border border-subtle"
              data-testid="share-modal-preview"
            />
          )}
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-subtle space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <button
              type="button"
              onClick={download}
              disabled={!pngUrl}
              data-testid="share-action-download"
              className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-sm border border-subtle hover:border-pos hover:bg-pos-soft/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download size={18} weight="bold" className="text-pos" />
              <span className="text-[10px] font-mono uppercase tracking-widest">Download</span>
            </button>
            <button
              type="button"
              onClick={copyImage}
              disabled={!pngUrl}
              data-testid="share-action-copy"
              className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-sm border border-subtle hover:border-info hover:bg-info-soft/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {copiedImage ? <Check size={18} weight="bold" className="text-pos" /> : <Copy size={18} weight="bold" className="text-info" />}
              <span className="text-[10px] font-mono uppercase tracking-widest">{copiedImage ? "Copied" : "Copy Img"}</span>
            </button>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-action-whatsapp"
              className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-sm border border-subtle hover:border-[#25D366] hover:bg-[#25D366]/10 transition"
            >
              <WhatsappLogo size={18} weight="fill" className="text-[#25D366]" />
              <span className="text-[10px] font-mono uppercase tracking-widest">WhatsApp</span>
            </a>
            <a
              href={twitterUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-action-twitter"
              className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-sm border border-subtle hover:border-[#1DA1F2] hover:bg-[#1DA1F2]/10 transition"
            >
              <TwitterLogo size={18} weight="fill" className="text-[#1DA1F2]" />
              <span className="text-[10px] font-mono uppercase tracking-widest">X / Twitter</span>
            </a>
          </div>

          <div className="flex items-center gap-2 text-[11px] text-muted-fg">
            <button
              type="button"
              onClick={copyLink}
              className="inline-flex items-center gap-1 text-pos hover:underline font-mono"
              data-testid="share-action-copy-link"
            >
              {copiedLink ? <Check size={11} weight="bold" /> : <Copy size={11} />}
              {copiedLink ? "Link copied" : "Copy link"}
            </button>
            <span>·</span>
            <span className="truncate">{shareUrl}</span>
          </div>

          {typeof navigator !== "undefined" && navigator.share && (
            <button
              type="button"
              onClick={shareNative}
              data-testid="share-action-native"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm text-xs font-medium border border-pos/40 text-pos hover:bg-pos-soft/10"
            >
              <ShareNetwork size={12} weight="bold" />
              More sharing options…
            </button>
          )}

          <div className="text-[10px] text-muted-fg/70 italic text-center">
            Image opens to your phone gallery on mobile · paste into any chat or post on socials.
          </div>
        </div>
      </div>
    </div>
  );
}
